import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatApiError } from '../lib/errors';
import { resolveAssetUrl } from '../lib/assets';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Check, Star, Tag, Truck, ArrowLeft, Send, ShieldCheck, Pencil, Lightbulb, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CatalogPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [catalogInfo, setCatalogInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [activeEntries, setActiveEntries] = useState([]);
  const [editingEntryId, setEditingEntryId] = useState(null); // null = create mode
  const [shopProfile, setShopProfile] = useState(null); // for legal-form gate

  // Form state
  const [selectedCatalogs, setSelectedCatalogs] = useState([]);
  const [extraCatalogs, setExtraCatalogs] = useState([]);
  const [formData, setFormData] = useState({
    contact_person: '', phone: '', email: '', description: '', address: ''
  });
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  const [plan, setPlan] = useState('yearly_promo');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [infoRes, browseRes, shopRes] = await Promise.all([
        axios.get(`${API}/catalog/info`),
        axios.get(`${API}/catalog/browse`),
        user
          ? axios.get(`${API}/shop-profile`).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      setCatalogInfo(infoRes.data);
      setActiveEntries(browseRes.data);
      // Pre-fill contact fields from the seller's shop profile.
      const shop = shopRes.data;
      setShopProfile(shop);
      if (shop) {
        setFormData((p) => ({
          ...p,
          contact_person: shop.legal_name || user?.name || p.contact_person,
          phone: shop.shop_phone || p.phone,
          email: shop.shop_email || user?.email || p.email,
          address: shop.legal_address || p.address,
        }));
      } else if (user) {
        setFormData((p) => ({
          ...p,
          contact_person: user.name || p.contact_person,
          email: user.email || p.email,
        }));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggleCatalog = (id) => {
    setSelectedCatalogs(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const toggleExtra = (id) => {
    setExtraCatalogs(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const toggleDelivery = (id) => {
    setDeliveryMethods(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };

  const totalCatalogs = selectedCatalogs.length + extraCatalogs.length;
  const monthlyPrice = catalogInfo?.pricing?.monthly ?? 10;
  const yearlyPromoPrice = catalogInfo?.pricing?.yearly_promo ?? 100;
  const totalPrice = plan === 'yearly_promo' ? yearlyPromoPrice : totalCatalogs * monthlyPrice;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    // Only require shop-profile contact info when CREATING a new application.
    // When editing, the entry already carries its own contact snapshot.
    if (!editingEntryId && (!formData.contact_person || !formData.phone || !formData.email)) {
      setError(language === 'lv'
        ? 'Lūdzu, vispirms aizpildi veikala profilu (kontaktpersona, telefons, e-pasts) — bez tā nevaram pieņemt kataloga pieteikumu.'
        : language === 'ru'
        ? 'Пожалуйста, сначала заполните профиль магазина (контактное лицо, телефон, e-mail) — без этого мы не можем принять заявку в каталог.'
        : 'Please complete your shop profile (contact person, phone, email) first — we cannot accept the catalog application without it.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      if (editingEntryId) {
        // Edit mode: PUT to update existing entry.
        const { data } = await axios.put(`${API}/catalog/applications/${editingEntryId}`, {
          selected_catalogs: selectedCatalogs,
          extra_catalogs: extraCatalogs,
          description: formData.description,
          delivery_methods: deliveryMethods,
          contact_person: formData.contact_person,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
        });
        // Optimistically update the local list so the card reflects the change
        // immediately, regardless of when the user navigates away.
        setActiveEntries((prev) => prev.map((e) => (
          e.id === editingEntryId
            ? {
                ...e,
                description: data.description ?? '',
                delivery_methods: data.delivery_methods ?? e.delivery_methods,
                selected_catalogs: data.selected_catalogs ?? e.selected_catalogs,
                extra_catalogs: data.extra_catalogs ?? e.extra_catalogs,
                catalogs: [
                  ...(data.selected_catalogs ?? e.selected_catalogs ?? []),
                  ...(data.extra_catalogs ?? e.extra_catalogs ?? []),
                ],
              }
            : e
        )));
      } else {
        await axios.post(`${API}/catalog/apply`, {
          selected_catalogs: selectedCatalogs,
          extra_catalogs: extraCatalogs,
          ...formData,
          delivery_methods: deliveryMethods,
          plan
        });
      }
      setSuccess(true);
    } catch (e) {
      setError(formatApiError(e, 'Error'));
    } finally { setSubmitting(false); }
  };

  const startEdit = (entry) => {
    setEditingEntryId(entry.id);
    setSelectedCatalogs(entry.selected_catalogs || []);
    setExtraCatalogs(entry.extra_catalogs || []);
    setDeliveryMethods(entry.delivery_methods || []);
    setPlan(entry.plan || 'yearly_promo');
    // Pull contact info from the entry's own snapshot (saved at creation/last save).
    // This makes edits independent of the current shop-profile state.
    setFormData((p) => ({
      ...p,
      description: entry.description || '',
      contact_person: entry.contact_person || p.contact_person || '',
      phone: entry.phone || p.phone || '',
      email: entry.email || p.email || '',
      address: entry.address || p.address || '',
    }));
    setShowApplyForm(true);
    setSuccess(false);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const exitForm = () => {
    setShowApplyForm(false);
    setEditingEntryId(null);
    setSuccess(false);
    setError('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97736]" />
      </div>
    );
  }

  const seasons = catalogInfo?.seasons || {};
  const catNames = catalogInfo?.catalog_names?.[language] || catalogInfo?.catalog_names?.lv || {};
  const extraNames = catalogInfo?.extra_catalog_names?.[language] || catalogInfo?.extra_catalog_names?.lv || {};
  const delivNames = catalogInfo?.delivery_method_names?.[language] || catalogInfo?.delivery_method_names?.lv || {};
  const deliveryList = catalogInfo?.delivery_methods || [];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="catalog-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-[#D97736]/10 rounded-2xl flex items-center justify-center">
            <BookOpen className="w-8 h-8 text-[#D97736]" />
          </div>
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-[#121212] mb-3">
            {language === 'lv' ? 'KoLV Katalogi' : language === 'ru' ? 'Каталоги KoLV' : 'KoLV Catalogs'}
          </h1>
          <p className="text-[#5C5C5C] max-w-xl mx-auto">
            {language === 'lv'
              ? 'Pievienojieties sezonas katalogiem un sasniedziet tūkstošiem pircēju visā Latvijā'
              : language === 'ru'
              ? 'Присоединяйтесь к сезонным каталогам и охватите тысячи покупателей по всей Латвии'
              : 'Join seasonal catalogs and reach thousands of buyers across Latvia'}
          </p>
        </div>

        {/* Catalog is now FREE for verified shops — show banner instead of pricing tiles */}
        {!editingEntryId && (
        <div className="mb-12">
          <div className="p-6 rounded-2xl border-2 border-[#D97736] bg-gradient-to-r from-[#D97736]/10 to-transparent">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[#D97736] flex items-center justify-center">
                <Check className="w-7 h-7 text-white" strokeWidth={3} />
              </div>
              <div className="flex-1 min-w-[280px]">
                <Badge className="bg-green-600 text-white border-0 mb-2">
                  {language === 'lv' ? 'BEZMAKSAS' : language === 'ru' ? 'БЕСПЛАТНО' : 'FREE'}
                </Badge>
                <h3 className="font-outfit font-bold text-2xl text-[#121212] mb-1">
                  {language === 'lv'
                    ? 'Katalogs ir BEZMAKSAS verificētiem veikaliem'
                    : language === 'ru'
                    ? 'Каталог БЕСПЛАТЕН для проверенных магазинов'
                    : 'Catalog is FREE for verified shops'}
                </h3>
                <p className="text-[#5C5C5C] text-sm">
                  {language === 'lv'
                    ? 'Verificē veikalu un pievienojies jebkuram katalogam bez maksas.'
                    : language === 'ru'
                    ? 'Пройдите верификацию и присоединяйтесь к любому каталогу бесплатно.'
                    : 'Get verified and join any catalog for free.'}
                </p>
              </div>
            </div>
          </div>
        </div>
        )}

        {!showApplyForm ? (
          <>
            {/* Season Catalogs Preview — each catalog shows logos of shops that joined it */}
            <div className="space-y-8 mb-12">
              {Object.entries(seasons).map(([qKey, season]) => (
                <div key={qKey} className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
                  <h3 className="font-outfit font-semibold text-lg text-[#121212] mb-4">
                    {season[language] || season.lv}
                  </h3>
                  <div className="space-y-3">
                    {season.catalogs.map(catId => {
                      const members = activeEntries.filter(e => e.catalogs.includes(catId));
                      return (
                        <div key={catId} className="flex items-center gap-3 flex-wrap">
                          <Link
                            to={`/catalog/${catId}`}
                            className="hover:scale-105 transition-transform"
                            data-testid={`catalog-chip-${catId}`}
                          >
                            <Badge variant="outline" className="px-3 py-1.5 text-sm whitespace-nowrap cursor-pointer hover:border-[#D97736] hover:bg-[#D97736]/5">
                              {catNames[catId] || catId}
                              {members.length > 0 && (
                                <span className="ml-1.5 text-[10px] text-[#8A8A8A]">({members.length})</span>
                              )}
                            </Badge>
                          </Link>
                          {members.length > 0 && (
                            <div className="flex -space-x-2">
                              {members.slice(0, 8).map(m => (
                                <Link
                                  key={m.id}
                                  to={`/shop/${m.user_id}`}
                                  title={m.shop_name}
                                  className="w-9 h-9 rounded-full border-2 border-white bg-[#FAFAFA] overflow-hidden hover:z-10 hover:scale-110 transition-transform flex items-center justify-center text-[11px] font-semibold text-[#5C5C5C]"
                                  data-testid={`catalog-${catId}-member-${m.user_id}`}
                                >
                                  {m.shop_profile?.shop_logo_url ? (
                                    <img
                                      src={resolveAssetUrl(m.shop_profile.shop_logo_url)}
                                      alt={m.shop_name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    (m.shop_name || '?').charAt(0).toUpperCase()
                                  )}
                                </Link>
                              ))}
                              {members.length > 8 && (
                                <div className="w-9 h-9 rounded-full border-2 border-white bg-[#F3F2EE] flex items-center justify-center text-[10px] font-medium text-[#5C5C5C]">
                                  +{members.length - 8}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center space-y-3">
              {(() => {
                // Catalog is FREE — gate is now: must be verified.
                // The onboarding wizard takes the user through profile → docs → verification.
                if (!user) return null;
                const isVerified = !!shopProfile?.is_verified;
                if (isVerified) return null;
                return (
                  <div className="max-w-2xl mx-auto p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-start gap-3" data-testid="catalog-verify-gate">
                    <ShieldCheck className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
                    <div className="text-left">
                      <p className="font-medium">
                        {language === 'lv'
                          ? 'Katalogs ir bezmaksas verificētiem veikaliem.'
                          : language === 'ru'
                          ? 'Каталог бесплатен для проверенных магазинов.'
                          : 'Catalog is free for verified shops.'}
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        {language === 'lv'
                          ? 'Aizpildi veikala profilu un nosūti pieprasījumu verifikācijai — pēc apstiprināšanas vari pievienoties jebkuram katalogam bez maksas.'
                          : language === 'ru'
                          ? 'Заполните профиль магазина и отправьте запрос на верификацию — после одобрения вы сможете присоединяться к любому каталогу бесплатно.'
                          : 'Fill out your shop profile and request verification — once approved you can join any catalog for free.'}
                      </p>
                      <Button
                        variant="link"
                        onClick={() => navigate('/shop-onboarding')}
                        className="px-0 mt-1 h-auto text-amber-900 underline"
                        data-testid="catalog-verify-gate-cta"
                      >
                        {language === 'lv' ? 'Sākt verifikāciju →' : language === 'ru' ? 'Начать верификацию →' : 'Start verification →'}
                      </Button>
                    </div>
                  </div>
                );
              })()}
              <Button
                onClick={() => {
                  if (!user) { navigate('/login'); return; }
                  // Re-check verification before opening the form.
                  if (!shopProfile?.is_verified) {
                    navigate('/shop-onboarding');
                    return;
                  }
                  setShowApplyForm(true);
                }}
                className="bg-[#D97736] hover:bg-[#C06528] text-white px-8 py-3 text-lg h-auto"
                data-testid="join-catalog-btn"
              >
                <BookOpen className="w-5 h-5 mr-2" />
                {language === 'lv' ? 'Pieteikties katalogam' : language === 'ru' ? 'Подать заявку в каталог' : 'Apply to Catalog'}
              </Button>
            </div>

            {/* Propose a new catalog */}
            <ProposeCatalogSection
              seasons={seasons}
              language={language}
              user={user}
              onAuthRequired={() => navigate('/login')}
            />

            {/* Active Entries */}
            {activeEntries.length > 0 && (
              <div className="mt-16">
                <h2 className="font-outfit text-2xl font-bold text-[#121212] mb-6">
                  {language === 'lv' ? 'Kataloga dalībnieki' : language === 'ru' ? 'Участники каталога' : 'Catalog Members'}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeEntries.map(entry => {
                    const myUserId = user?._id || user?.id;
                    // Only the formal catalog_application can be edited here. Shops that
                    // appear only because their listings are tagged manage those catalogs
                    // through each listing's own edit form.
                    const isMine = !!myUserId && entry.user_id === myUserId && entry.source !== 'listing';
                    return (
                    <div
                      key={entry.id}
                      className="relative bg-white border border-[#E5E4E0] rounded-xl p-5 hover:-translate-y-1 hover:shadow-lg transition-all"
                    >
                      {isMine && (
                        <button
                          type="button"
                          onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); startEdit(entry); }}
                          className="absolute top-3 right-3 z-10 flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-[#D97736] text-[#D97736] bg-white hover:bg-[#D97736] hover:text-white transition-colors"
                          data-testid={`edit-catalog-entry-${entry.id}`}
                        >
                          <Pencil className="w-3 h-3" />
                          {language === 'lv' ? 'Rediģēt' : language === 'ru' ? 'Редактировать' : 'Edit'}
                        </button>
                      )}
                      <Link to={`/shop/${entry.user_id}`} className="block">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-xl bg-[#FAFAFA] border border-[#E5E4E0] overflow-hidden flex items-center justify-center text-[#D97736] font-bold flex-shrink-0">
                            {entry.shop_profile?.shop_logo_url ? (
                              <img
                                src={resolveAssetUrl(entry.shop_profile.shop_logo_url)}
                                alt={entry.shop_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              (entry.shop_name || '?').charAt(0).toUpperCase()
                            )}
                          </div>
                          <h3 className="font-semibold text-[#121212] pr-16">{entry.shop_name}</h3>
                        </div>
                        <p className="text-sm text-[#5C5C5C] line-clamp-2 mb-3">
                          {entry.description || (
                            <span className="italic text-[#8A8A8A]">
                              {language === 'lv'
                                ? 'Veikals piedalās ar saviem sludinājumiem.'
                                : language === 'ru'
                                ? 'Магазин участвует со своими объявлениями.'
                                : 'Shop participates with its listings.'}
                            </span>
                          )}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {entry.catalogs.slice(0, 3).map(c => (
                            <Badge key={c} variant="outline" className="text-xs">{catNames[c] || extraNames[c] || c}</Badge>
                          ))}
                          {entry.catalogs.length > 3 && (
                            <Badge variant="outline" className="text-xs">+{entry.catalogs.length - 3}</Badge>
                          )}
                        </div>
                      </Link>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : success ? (
          <div className="text-center py-12 bg-white border border-[#E5E4E0] rounded-2xl">
            <ShieldCheck className="w-16 h-16 mx-auto mb-4 text-green-600" />
            <h2 className="font-outfit text-2xl font-bold text-[#121212] mb-2">
              {editingEntryId
                ? (language === 'lv' ? 'Izmaiņas saglabātas!' : language === 'ru' ? 'Изменения сохранены!' : 'Changes Saved!')
                : (language === 'lv' ? 'Pieteikums iesniegts!' : language === 'ru' ? 'Заявка отправлена!' : 'Application Submitted!')}
            </h2>
            <p className="text-[#5C5C5C] mb-6">
              {editingEntryId
                ? (language === 'lv' ? 'Tavs kataloga ieraksts ir atjaunināts.' : language === 'ru' ? 'Ваша запись в каталоге обновлена.' : 'Your catalog entry has been updated.')
                : (language === 'lv' ? 'Mēs sazināsimies ar jums drīzumā.' : language === 'ru' ? 'Мы свяжемся с вами в ближайшее время.' : 'We will contact you soon.')}
            </p>
            <Button onClick={() => { exitForm(); fetchData(); }} className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="catalog-success-back-btn">
              {language === 'lv' ? 'Atpakaļ uz katalogiem' : language === 'ru' ? 'Назад к каталогам' : 'Back to Catalogs'}
            </Button>
          </div>
        ) : (
          /* Apply Form */
          <form onSubmit={handleSubmit} className="space-y-8">
            <button type="button" onClick={exitForm} className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212]">
              <ArrowLeft className="w-4 h-4" /> {language === 'lv' ? 'Atpakaļ' : language === 'ru' ? 'Назад' : 'Back'}
            </button>

            {editingEntryId && (
              <div className="p-4 bg-[#D97736]/5 border border-[#D97736]/20 rounded-xl text-sm text-[#5C5C5C] flex items-start gap-2" data-testid="edit-mode-banner">
                <Pencil className="w-4 h-4 text-[#D97736] mt-0.5 flex-shrink-0" />
                <span>
                  {language === 'lv'
                    ? 'Tu rediģē savu esošo kataloga ierakstu. Apraksts, kontakti un piegādes veidi tiek atjaunināti uzreiz — bez administratora apstiprinājuma. Aktīviem ierakstiem katalogu sarakstu var mainīt tikai administrators.'
                    : language === 'ru'
                    ? 'Вы редактируете существующую запись каталога. Описание, контакты и способы доставки обновляются сразу — без одобрения администратора. Для активных записей список каталогов может изменить только администратор.'
                    : 'You are editing your existing catalog entry. Description, contact details and delivery methods update instantly — no admin approval needed. For active entries, the catalog list can only be changed by an administrator.'}
                </span>
              </div>
            )}

            {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>}

            {/* Select Catalogs */}
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
              <h3 className="font-outfit font-semibold text-lg text-[#121212] mb-4">{language === 'lv' ? 'Izvēlieties katalogus' : language === 'ru' ? 'Выберите каталоги' : 'Select Catalogs'}</h3>
              {Object.entries(seasons).map(([qKey, season]) => (
                <div key={qKey} className="mb-6">
                  <h4 className="font-medium text-[#5C5C5C] mb-3 text-sm italic">{season[language] || season.lv}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {season.catalogs.map(catId => (
                      <label key={catId} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F3F2EE] cursor-pointer transition-colors">
                        <Checkbox
                          checked={selectedCatalogs.includes(catId)}
                          onCheckedChange={() => toggleCatalog(catId)}
                          className="border-[#E5E4E0] data-[state=checked]:bg-[#D97736] data-[state=checked]:border-[#D97736]"
                        />
                        <span className="text-sm text-[#121212]">{catNames[catId] || catId}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              <h4 className="font-medium text-[#5C5C5C] mb-3 text-sm italic">{language === 'lv' ? 'Papildu katalogi' : language === 'ru' ? 'Дополнительные каталоги' : 'Extra Catalogs'}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(catalogInfo?.extra_catalogs || []).map(catId => (
                  <label key={catId} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F3F2EE] cursor-pointer transition-colors">
                    <Checkbox
                      checked={extraCatalogs.includes(catId)}
                      onCheckedChange={() => toggleExtra(catId)}
                      className="border-[#E5E4E0] data-[state=checked]:bg-[#D97736] data-[state=checked]:border-[#D97736]"
                    />
                    <span className="text-sm text-[#121212]">{extraNames[catId] || catId}</span>
                  </label>
                ))}
              </div>

              <div className="mt-4 p-4 bg-[#D97736]/5 rounded-xl flex items-center justify-between">
                <span className="text-sm text-[#5C5C5C]">{language === 'lv' ? 'Izvēlēti' : language === 'ru' ? 'Выбрано' : 'Selected'}: <b>{totalCatalogs}</b></span>
                {!editingEntryId && (
                  <span className="font-outfit font-bold text-[#D97736] text-lg">{totalPrice} EUR</span>
                )}
              </div>
            </div>

            {/* Contact Info — auto-filled from the shop profile (single source of truth). */}
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="catalog-contact-section">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="font-outfit font-semibold text-lg text-[#121212]">
                    {language === 'lv' ? 'Kontaktinformācija' : language === 'ru' ? 'Контактная информация' : 'Contact Information'}
                  </h3>
                  <p className="text-xs text-[#5C5C5C] mt-1">
                    {language === 'lv'
                      ? 'Šī informācija tiek paņemta no Tava veikala profila. Lai mainītu, dodies uz “Rediģēt veikalu”.'
                      : language === 'ru'
                      ? 'Эта информация берётся из профиля вашего магазина. Чтобы изменить, перейдите в «Редактировать магазин».'
                      : 'This information is pulled from your shop profile. To change it, edit your shop.'}
                  </p>
                </div>
                <a
                  href="/shop-profile"
                  className="text-xs px-3 py-1.5 rounded-full border border-[#E5E4E0] text-[#5C5C5C] hover:border-[#D97736] hover:text-[#D97736] whitespace-nowrap"
                  data-testid="catalog-edit-shop-link"
                >
                  {language === 'lv' ? 'Rediģēt veikalu' : language === 'ru' ? 'Редактировать магазин' : 'Edit shop'}
                </a>
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-[#8A8A8A] text-xs">{language === 'lv' ? 'Kontaktpersona' : language === 'ru' ? 'Контактное лицо' : 'Contact person'}</dt>
                  <dd className="text-[#121212] font-medium" data-testid="catalog-contact-person">
                    {formData.contact_person || <span className="text-red-500">{language === 'lv' ? '— nav norādīts —' : language === 'ru' ? '— не указано —' : '— not set —'}</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-[#8A8A8A] text-xs">{language === 'lv' ? 'Telefons' : language === 'ru' ? 'Телефон' : 'Phone'}</dt>
                  <dd className="text-[#121212] font-medium" data-testid="catalog-phone">
                    {formData.phone || <span className="text-red-500">{language === 'lv' ? '— nav norādīts —' : language === 'ru' ? '— не указано —' : '— not set —'}</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-[#8A8A8A] text-xs">E-pasts</dt>
                  <dd className="text-[#121212] font-medium" data-testid="catalog-email">
                    {formData.email || <span className="text-red-500">{language === 'lv' ? '— nav norādīts —' : language === 'ru' ? '— не указано —' : '— not set —'}</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-[#8A8A8A] text-xs">{language === 'lv' ? 'Adrese' : language === 'ru' ? 'Адрес' : 'Address'}</dt>
                  <dd className="text-[#121212] font-medium" data-testid="catalog-address">
                    {formData.address || <span className="text-[#8A8A8A]">{language === 'lv' ? '— nav norādīts —' : language === 'ru' ? '— не указано —' : '— not set —'}</span>}
                  </dd>
                </div>
              </dl>

              <div className="mt-5 space-y-2">
                <Label>
                  {language === 'lv' ? 'Apraksts katalogam' : language === 'ru' ? 'Описание для каталога' : 'Catalog description'}
                  <span className="ml-1 text-xs font-normal text-[#8A8A8A]">
                    ({language === 'lv' ? 'neobligāts' : language === 'ru' ? 'необязательно' : 'optional'})
                  </span>
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={e => setFormData(p => ({...p, description: e.target.value}))}
                  rows={3}
                  className="bg-[#FAFAFA] border-[#E5E4E0] resize-none"
                  placeholder={language === 'lv' ? 'Īss apraksts, ko piedāvā šajā katalogā... (neobligāts)' : language === 'ru' ? 'Краткое описание того, что вы предлагаете в этом каталоге... (необязательно)' : 'Short description of what you offer in this catalog... (optional)'}
                  data-testid="catalog-description-input"
                />
              </div>
            </div>

            {/* Delivery Methods */}
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
              <h3 className="font-outfit font-semibold text-lg text-[#121212] mb-4 flex items-center gap-2">
                <Truck className="w-5 h-5 text-[#D97736]" />
                {language === 'lv' ? 'Kā pakalpojums/prece nonāk pie klienta?' : language === 'ru' ? 'Как товар/услуга доходит до клиента?' : 'How does the product reach the customer?'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {deliveryList.map(dm => (
                  <label key={dm} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F3F2EE] cursor-pointer transition-colors">
                    <Checkbox
                      checked={deliveryMethods.includes(dm)}
                      onCheckedChange={() => toggleDelivery(dm)}
                      className="border-[#E5E4E0] data-[state=checked]:bg-[#D97736] data-[state=checked]:border-[#D97736]"
                    />
                    <span className="text-sm text-[#121212]">{delivNames[dm] || dm}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={submitting || totalCatalogs === 0}
              className="w-full h-12 bg-[#D97736] hover:bg-[#C06528] text-white text-lg"
              data-testid="submit-catalog-btn"
            >
              {submitting ? (language === 'lv' ? 'Iesniedz...' : language === 'ru' ? 'Отправка...' : 'Submitting...') : (
                <span className="flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  {editingEntryId
                    ? (language === 'lv' ? 'Saglabāt izmaiņas' : language === 'ru' ? 'Сохранить изменения' : 'Save changes')
                    : (language === 'lv' ? `Iesniegt pieteikumu (${totalPrice} EUR)` : language === 'ru' ? `Подать заявку (${totalPrice} EUR)` : `Submit Application (${totalPrice} EUR)`)}
                </span>
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}


function ProposeCatalogSection({ seasons, language, user, onAuthRequired }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [seasonKey, setSeasonKey] = useState('');
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setTitle(''); setSeasonKey(''); setDescription(''); setReason('');
    setSuccess(false); setError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { onAuthRequired(); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/catalog/proposals`, {
        title: title.trim(),
        season_key: seasonKey || null,
        description: description.trim(),
        reason: reason.trim(),
      });
      setSuccess(true);
      setTimeout(() => { setOpen(false); reset(); }, 2500);
    } catch (err) {
      setError(formatApiError(err, language === 'lv' ? 'Kļūda' : language === 'ru' ? 'Ошибка' : 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-16 bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="propose-catalog-section">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="propose-catalog-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#D97736]/10 flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-[#D97736]" />
          </div>
          <div>
            <h3 className="font-outfit font-semibold text-[#121212]">
              {language === 'lv' ? 'Ierosini jaunu katalogu' : language === 'ru' ? 'Предложить новый каталог' : 'Propose a new catalog'}
            </h3>
            <p className="text-xs text-[#5C5C5C]">
              {language === 'lv'
                ? 'Trūkst kāda kataloga? Iesniedz ideju — administrators izskatīs.'
                : language === 'ru'
                ? 'Не хватает какого-то каталога? Отправьте идею — администраторы рассмотрят.'
                : "Catalog you'd love missing? Send the idea — admins review."}
            </p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-[#5C5C5C] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <form onSubmit={submit} className="mt-5 pt-5 border-t border-[#E5E4E0] space-y-4" data-testid="propose-catalog-form">
          {success ? (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-emerald-700" data-testid="propose-catalog-success">
              <ShieldCheck className="w-5 h-5" />
              {language === 'lv' ? 'Paldies! Ideja nosūtīta administratoram.' : language === 'ru' ? 'Спасибо! Идея отправлена администраторам.' : 'Thanks! Idea sent to admins.'}
            </div>
          ) : (
            <>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>
              )}
              <div>
                <Label>{language === 'lv' ? 'Kataloga nosaukums' : language === 'ru' ? 'Название каталога' : 'Catalog name'} *</Label>
                <Input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  required
                  minLength={3}
                  maxLength={120}
                  placeholder={language === 'lv' ? 'piem., Bērnu dārza preču katalogs' : language === 'ru' ? 'напр., Каталог детских садовых товаров' : 'e.g., Garden tools for kids'}
                  className="bg-[#FAFAFA] border-[#E5E4E0] mt-1"
                  data-testid="propose-catalog-title"
                />
              </div>
              <div>
                <Label>{language === 'lv' ? 'Sezona' : language === 'ru' ? 'Сезон' : 'Season'}</Label>
                <select
                  value={seasonKey}
                  onChange={e => setSeasonKey(e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-[#FAFAFA] border border-[#E5E4E0] text-sm"
                  data-testid="propose-catalog-season"
                >
                  <option value="">{language === 'lv' ? '— jebkura / cita —' : language === 'ru' ? '— любой / другой —' : '— any / other —'}</option>
                  {Object.entries(seasons || {}).map(([k, s]) => (
                    <option key={k} value={k}>{s[language] || s.lv}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>{language === 'lv' ? 'Apraksts' : language === 'ru' ? 'Описание' : 'Description'}</Label>
                <Textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder={language === 'lv' ? 'Kādu produktu/pakalpojumu klāstu šis katalogs aptvers?' : language === 'ru' ? 'Какой ассортимент товаров/услуг охватит этот каталог?' : 'What product / service range will this catalog cover?'}
                  className="bg-[#FAFAFA] border-[#E5E4E0] mt-1 resize-none"
                  data-testid="propose-catalog-description"
                />
              </div>
              <div>
                <Label>{language === 'lv' ? 'Kāpēc tas vajadzīgs?' : language === 'ru' ? 'Почему это нужно?' : 'Why is it needed?'}</Label>
                <Textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder={language === 'lv' ? 'Īss paskaidrojums...' : language === 'ru' ? 'Краткое пояснение...' : 'A short note...'}
                  className="bg-[#FAFAFA] border-[#E5E4E0] mt-1 resize-none"
                  data-testid="propose-catalog-reason"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || title.trim().length < 3}
                className="bg-[#D97736] hover:bg-[#C06528] text-white"
                data-testid="propose-catalog-submit"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting
                  ? (language === 'lv' ? 'Sūta...' : language === 'ru' ? 'Отправка...' : 'Sending...')
                  : (language === 'lv' ? 'Iesniegt ideju' : language === 'ru' ? 'Отправить идею' : 'Submit idea')}
              </Button>
            </>
          )}
        </form>
      )}
    </div>
  );
}
